import { useEffect, useState } from "react"
import { Card, CardContent, Typography, List, Box, Grid, Button, Divider } from "@mui/material"
import { TrendingUp, ShoppingCart, Visibility, ThumbUp } from "@mui/icons-material"
import GroupIcon from "@mui/icons-material/Group"
import Activity from "./Activity"
import { useApiService } from "../hooks/Api/useApiService"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import T from "../hooks/Language/T"
import useTranslationSharie from "../hooks/Language/useTranslation"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const Activities = () => {
  const [activities, setActivities] = useState([])
  const [skillsLength, setSkillsLength] = useState(0)
  const [groupsLength, setGroupsLength] = useState(0)
  const [showMore, setShowMore] = useState(false)
  const [stats, setStats] = useState({
    Skills: 0,
    Groups: 0,
    Purchases: 0,
    Views: 0,
    Likes: 0,
  })
  const [interactionSummary, setInteractionSummary] = useState({
    mostLikedSkill: "",
    mostViewedSkill: "",
  })
  const [activityChartData, setActivityChartData] = useState({})
  const { postData } = useApiService()
  const t = useTranslationSharie()

  useEffect(() => {
    postData("sharie/getallnotificationsbyuser", { user_name: localStorage.getItem("user") })
      .then((data) => {
        setActivities(data?.notifications || [])
        return postData("skills/getskillsbyuser", { user_name: localStorage.getItem("user") })
      })
      .then((data) => {
        setSkillsLength(data?.skills.length)
        setGroupsLength(data?.groups_length)
      })
  }, [])

  useEffect(() => {
    makeStats()
    generateInteractionSummary()
    generateActivityChart()
  }, [activities, skillsLength])

  const makeStats = () => {
    let purchases = 0
    let views = 0
    let likes = 0
    const currentUser = localStorage.getItem("user")

    activities.forEach((el) => {
      if (el.from === currentUser) {
        if (el.type === "buy skill") purchases += 1
      } else if (el.to === currentUser) {
        if (el.type === "view skill" || el.type === "view profile") views += 1
        if (el.type === "like") likes += 1
      }
    })

    setStats({
      Skills: skillsLength,
      Groups: groupsLength,
      Purchases: purchases,
      Views: views,
      Likes: likes,
    })
  }

  const generateInteractionSummary = () => {
    const currentUser = localStorage.getItem("user")

    let mostLikedSkill = ""
    let mostViewedSkill = ""

    const likedSkills = []
    const viewedSkills = []

    activities.forEach((activity) => {
      if (activity.to === currentUser) {
        if (activity.type === "like" && activity.skill_name) {
          likedSkills.push(activity.skill_name)
        }
        if (
          (activity.type === "view skill" || activity.type === "view profile") &&
          activity.skill_name
        ) {
          viewedSkills.push(activity.skill_name)
        }
      }
    })

    mostLikedSkill = findMostFrequentSkill(likedSkills)
    mostViewedSkill = findMostFrequentSkill(viewedSkills)

    setInteractionSummary({
      mostLikedSkill,
      mostViewedSkill,
    })
  }

  const findMostFrequentSkill = (skillsArray) => {
    const skillCount = {}
    let mostFrequent = ""
    let maxCount = 0

    skillsArray.forEach((skill) => {
      skillCount[skill] = (skillCount[skill] || 0) + 1
      if (skillCount[skill] > maxCount) {
        maxCount = skillCount[skill]
        mostFrequent = skill
      }
    })

    return mostFrequent
  }

  const generateActivityChart = () => {
    if (activities.length === 0) return

    const activityCounts = {}
    activities.forEach((activity) => {
      const formattedDate = activity.date_time.replace(" ", "T") // Replaces the space with "T"
      const activityDate = new Date(formattedDate)
      const day = activityDate.toLocaleDateString()

      if (activityCounts[day]) {
        activityCounts[day]++
      } else {
        activityCounts[day] = 1
      }
    })

    const chartLabels = Object.keys(activityCounts)
    const chartData = Object.values(activityCounts)

    setActivityChartData({
      labels: chartLabels,
      datasets: [
        {
          label: "Activity Per Day",
          data: chartData,
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    })
  }

  return (
    <T>
      <Box>
        {/* Box containing Statistics and Chart */}
        <Grid container spacing={2}>
          {/* Statistics Card */}
          <Grid item xs={12} md={4}>
            <Card style={{ borderRadius: "0.8rem" }}>
              <CardContent>
                <Typography variant='h5' gutterBottom>
                  Your Statistics
                </Typography>
                <Box display='flex' flexDirection='column' gap={2} width='100%' height='437px'>
                  {Object.entries(stats).map(([key, value]) => (
                    <Box key={key} display='flex' gap={2} alignItems='center'>
                      {key === "Skills" && <TrendingUp color='primary' />}
                      {key === "Groups" && <GroupIcon color='primary' />}
                      {key === "Purchases" && <ShoppingCart color='primary' />}
                      {key === "Views" && <Visibility color='primary' />}
                      {key === "Likes" && <ThumbUp color='primary' />}
                      <Typography variant='body1'>{key}:</Typography>
                      <Typography variant='body1' fontWeight='bold'>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant='h6' gutterBottom>
                  Favorites
                </Typography>
                {interactionSummary ? (
                  <List>
                    {interactionSummary.mostLikedSkill && (
                      <Typography variant='body2'>
                        <ThumbUp fontSize='small' sx={{ mr: 1, ml: 1 }} /> {t("Most Liked Skill")} :{" "}
                        {interactionSummary.mostLikedSkill}
                      </Typography>
                    )}
                    {interactionSummary.mostViewedSkill && (
                      <Typography variant='body2'>
                        <Visibility fontSize='small' sx={{ mr: 1, ml: 1 }} />
                        {t("Most Viewed Skill")} : {interactionSummary.mostViewedSkill}
                      </Typography>
                    )}
                  </List>
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    No recent interactions available.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Activity Chart */}
          <Grid item xs={12} md={8}>
            <Card style={{ borderRadius: "0.8rem" }}>
              <CardContent>
                {activityChartData?.labels?.length > 0 ? (
                  <Bar data={activityChartData} options={{ responsive: true }} />
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    No activity data available for the chart.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        {/* Activity List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='h5' gutterBottom>
                Recent Activity
              </Typography>
              <List>
                {activities?.slice(0, showMore ? activities.length : 10).map((activity, index) => (
                  <Activity key={index} activity={activity} />
                ))}
              </List>
              {activities.length > 10 && (
                <Box textAlign='center' mt={2}>
                  <Button variant='outlined' onClick={() => setShowMore(!showMore)}>
                    {showMore ? "Show Less" : "Show More"}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Box>
    </T>
  )
}

export default Activities
